import { api } from "../../app/api/api";
import { ISO_DATE_LENGTH } from "../../app/utils/isoDate";

export enum MemberPromotionTemplateRewardMode {
  PriceAdjustment = 1,
  WalletCredit = 2,
  Choice = 3
}

export enum MemberPromotionRewardKind {
  PriceAdjustment = 1,
  WalletCredit = 2
}

export enum MemberPromotionDurationUnit {
  Days = 1,
  Weeks = 2,
  Months = 3,
  ContributionCycles = 4
}

export enum MemberPromotionRedemptionStartMode {
  NextDueDate = 1,
  FixedDate = 2,
  BeforeContractRenewal = 3,
  AfterContractEnd = 4
}

export enum MemberPromotionQualificationTiming {
  Immediate = 1,
  FixedDate = 2,
  AfterReferredContractStart = 3
}

export enum MemberPromotionQualificationCondition {
  ActiveOnDate = 1,
  NoCancellationRecorded = 2,
  NoCancellationEffective = 3,
  ManualReview = 4
}

export enum MemberPromotionLaterCancellationBehavior {
  IgnoreAfterQualification = 1,
  BlockUntilRedemption = 2,
  RevokeUntilBenefitEnd = 3,
  ManualReview = 4
}

export enum MemberPromotionGrantStatus {
  Pending = 1,
  Qualified = 2,
  Redeemed = 3,
  Rejected = 4,
  Cancelled = 5,
  NeedsReview = 6
}

export type MemberPromotionTemplateDto = {
  id: string;
  name: string;
  description?: string | null;
  allowedStudioIds: string[];
  rewardMode: MemberPromotionTemplateRewardMode;
  priceAdjustmentType?: number | null;
  priceAdjustmentValue?: number | null;
  priceAdjustmentCycleUnit?: number | null;
  durationUnit: MemberPromotionDurationUnit;
  durationValue: number;
  startMode: MemberPromotionRedemptionStartMode;
  qualificationTiming: MemberPromotionQualificationTiming;
  fixedQualificationDate?: string | null;
  qualificationOffsetUnit?: MemberPromotionDurationUnit | null;
  qualificationOffsetValue?: number | null;
  qualificationCondition: MemberPromotionQualificationCondition;
  laterCancellationBehavior: MemberPromotionLaterCancellationBehavior;
  walletProductId?: string | null;
  walletProductName?: string | null;
  walletCreditAmount?: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type UpsertMemberPromotionTemplateDto = Omit<MemberPromotionTemplateDto, "id" | "walletProductName" | "createdAt" | "updatedAt">;

export type MemberPromotionGrantDto = {
  id: string;
  templateId: string;
  templateName?: string | null;
  referrerPersonId: string;
  referrerPersonName: string;
  referredPersonId: string;
  referredPersonName: string;
  referredContractPackageId?: string | null;
  targetContractPackageId?: string | null;
  targetContractId?: string | null;
  targetContractName?: string | null;
  targetWalletId?: string | null;
  targetWalletName?: string | null;
  targetWalletProductId?: string | null;
  rewardKind: MemberPromotionRewardKind;
  status: MemberPromotionGrantStatus;
  referralDate: string;
  recordedAt: string;
  qualificationDate?: string | null;
  requestedStartDate?: string | null;
  calculatedStartDate?: string | null;
  calculatedEndDate?: string | null;
  note?: string | null;
  decisionReason?: string | null;
  contractPriceAdjustmentId?: string | null;
  walletTransactionId?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type UpsertMemberPromotionGrantDto = {
  templateId: string;
  referrerPersonId: string;
  referredPersonId: string;
  referredContractPackageId?: string | null;
  targetContractPackageId?: string | null;
  targetContractId?: string | null;
  targetWalletId?: string | null;
  targetWalletProductId?: string | null;
  rewardKind?: MemberPromotionRewardKind | null;
  referralDate: string;
  requestedStartDate?: string | null;
  note?: string | null;
};

export type PersonOptionDto = {
  id: string;
  firstName: string;
  lastName: string;
};

export type WalletProductOptionDto = {
  id: string;
  name: string;
  isActive: boolean;
};

export type ContractPackageOptionDto = {
  id: string;
  planName?: string | null;
  startDate?: string | null;
  primaryContractEndDate?: string | null;
  effectiveMembershipEndDate?: string | null;
  status?: number | null;
};

export const memberPromotionApi = {
  listTemplates: (includeInactive = false, withStudioScope: (url: string) => string = (url) => url) =>
    api.get<MemberPromotionTemplateDto[]>(withStudioScope(`/api/v1/member-promotions/templates?includeInactive=${includeInactive ? "true" : "false"}`)),
  createTemplate: (body: UpsertMemberPromotionTemplateDto) => api.post<MemberPromotionTemplateDto>("/api/v1/member-promotions/templates", body),
  updateTemplate: (id: string, body: UpsertMemberPromotionTemplateDto) => api.put<MemberPromotionTemplateDto>(`/api/v1/member-promotions/templates/${id}`, body),
  deleteTemplate: (id: string) => api.delete<void>(`/api/v1/member-promotions/templates/${id}`),
  listGrants: (params?: { personId?: string; status?: MemberPromotionGrantStatus }, withStudioScope: (url: string) => string = (url) => url) => {
    const qs = new URLSearchParams();
    if (params?.personId) qs.set("personId", params.personId);
    if (params?.status) qs.set("status", String(params.status));
    return api.get<MemberPromotionGrantDto[]>(withStudioScope(`/api/v1/member-promotions/grants${qs.toString() ? `?${qs.toString()}` : ""}`));
  },
  createGrant: (body: UpsertMemberPromotionGrantDto) => api.post<MemberPromotionGrantDto>("/api/v1/member-promotions/grants", body),
  updateGrant: (id: string, body: UpsertMemberPromotionGrantDto) => api.put<MemberPromotionGrantDto>(`/api/v1/member-promotions/grants/${id}`, body),
  qualifyGrant: (id: string) => api.post<MemberPromotionGrantDto>(`/api/v1/member-promotions/grants/${id}/qualify`),
  redeemGrant: (id: string) => api.post<MemberPromotionGrantDto>(`/api/v1/member-promotions/grants/${id}/redeem`),
  cancelGrant: (id: string, reason?: string) => api.post<MemberPromotionGrantDto>(`/api/v1/member-promotions/grants/${id}/cancel`, { reason }),
  searchPersons: (q: string) => api.get<PersonOptionDto[]>(`/api/v1/persons?q=${encodeURIComponent(q)}&limit=20`),
  listContractPackages: (personId: string) => api.get<ContractPackageOptionDto[]>(`/api/v1/contract-packages?personId=${encodeURIComponent(personId)}`),
  listWalletProducts: () => api.get<WalletProductOptionDto[]>("/api/v1/wallet-products")
};

export function personOptionLabel(person: PersonOptionDto) {
  return `${person.firstName || ""} ${person.lastName || ""}`.trim();
}

export function contractPackageOptionLabel(contractPackage: ContractPackageOptionDto, formatDate?: (value: string) => string) {
  const name = contractPackage.planName || "";
  const formatDateOnly = (value?: string | null) => {
    const isoDate = value ? String(value).slice(0, ISO_DATE_LENGTH) : "";
    return isoDate && formatDate ? formatDate(isoDate) : isoDate;
  };
  const start = formatDateOnly(contractPackage.startDate);
  const end = formatDateOnly(contractPackage.effectiveMembershipEndDate || contractPackage.primaryContractEndDate);
  const period = start && end ? `${start} - ${end}` : start || end;
  return period ? `${name} (${period})` : name || contractPackage.id;
}
